import { ref, reactive, provide, inject } from 'vue';

const DRAG_DROP_KEY = Symbol('dragDrop');
const CROSS_WINDOW_DRAG_STORAGE_KEY = 'planner-cross-window-drag';
const CROSS_WINDOW_MOVE_STORAGE_KEY = 'planner-cross-window-move';

const resolvePlannerDate = (plannerDate) => {
    if (typeof plannerDate === 'function') {
        return plannerDate();
    }

    if (plannerDate && typeof plannerDate === 'object' && 'value' in plannerDate) {
        return plannerDate.value;
    }

    return plannerDate;
};

const buildScopedStorageKey = (baseKey, plannerDate) => {
    const resolvedDate = resolvePlannerDate(plannerDate);

    if (!resolvedDate) return baseKey;

    return `${baseKey}:${resolvedDate}`;
};

const parseDragPayload = (event) => {
    if (!event?.dataTransfer) return null;

    const rawPayload = event.dataTransfer.getData('text/plain') || event.dataTransfer.getData('text');

    if (!rawPayload) return null;

    try {
        return JSON.parse(rawPayload);
    } catch (e) {
        return null;
    }
};

/**
 * Creates a drag and drop context for managing drag state across components
 * Use this in the parent component that contains all dropzones
 */
export function createDragDropContext(plannerDate = null) {
    const getCrossWindowDragStorageKey = () => buildScopedStorageKey(CROSS_WINDOW_DRAG_STORAGE_KEY, plannerDate);
    const getCrossWindowMoveStorageKey = () => buildScopedStorageKey(CROSS_WINDOW_MOVE_STORAGE_KEY, plannerDate);

    const state = reactive({
        isDragging: false,
        draggedItem: null,
        draggedIndex: null,
        sourceDropzoneId: null,
        targetDropzoneId: null,
        hoveredDropzoneId: null,
        dragOverIndex: null,
        ghostElement: null,
        ghostOffsetX: 0,
        ghostOffsetY: 0,
        lastValidX: 0,
        lastValidY: 0,
        externalDragPayload: null,
        externalPreviewActive: false,
    });

    const dropzones = ref(new Map());
    let windowDragListenersBound = false;

    const setStoredExternalDragPayload = (payload) => {
        try {
            if (!payload) {
                localStorage.removeItem(getCrossWindowDragStorageKey());
                return;
            }

            localStorage.setItem(getCrossWindowDragStorageKey(), JSON.stringify({
                payload,
                updatedAt: Date.now(),
            }));
        } catch (e) {
            // Ignore storage failures in restrictive browser modes
        }
    };

    const setStoredCrossWindowMovePayload = (payload) => {
        try {
            if (!payload) return;

            localStorage.setItem(getCrossWindowMoveStorageKey(), JSON.stringify({
                payload,
                updatedAt: Date.now(),
            }));
        } catch (e) {
            // Ignore storage failures in restrictive browser modes
        }
    };

    const getStoredExternalDragPayload = () => {
        try {
            const raw = localStorage.getItem(getCrossWindowDragStorageKey());
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed?.payload ?? null;
        } catch (e) {
            return null;
        }
    };

    const registerDropzone = (id, { getItems, setItems, onReorder }) => {
        dropzones.value.set(id, { getItems, setItems, onReorder });
    };

    const unregisterDropzone = (id) => {
        dropzones.value.delete(id);
    };

    const bindWindowDragListeners = () => {
        if (windowDragListenersBound || typeof window === 'undefined') return;

        const handleWindowDragEnter = (event) => {
            if (state.isDragging) {
                if (!state.ghostElement && state.draggedItem?.id) {
                    const sourceElement = document.querySelector(`[data-movement-id="${state.draggedItem.id}"]`);
                    if (sourceElement) {
                        createGhostElement(sourceElement, event, state.ghostOffsetX, state.ghostOffsetY);
                    }
                }
                return;
            }

            if (state.externalPreviewActive) return;

            const payload = parseDragPayload(event) || state.externalDragPayload || getStoredExternalDragPayload();
            if (payload) {
                startExternalDrag(payload, event);
            }
        };

        const handleWindowDragOver = (event) => {
            // Source-window drags should keep moving even over non-droppable surfaces.
            if (state.isDragging) {
                updateGhostPosition(event);
                return;
            }

            event.preventDefault();

            const payload = state.externalDragPayload || parseDragPayload(event) || getStoredExternalDragPayload();
            if (payload && !state.externalPreviewActive) {
                startExternalDrag(payload, event);
            }

            if (state.externalPreviewActive || state.ghostElement) {
                updateGhostPosition(event);
            }
        };

        const handleWindowDrop = (event) => {
            if (state.isDragging) return;

            const payload = state.externalDragPayload || parseDragPayload(event) || getStoredExternalDragPayload();
            if (payload) {
                const targetDropzoneId = state.targetDropzoneId || payload.dropzoneId;
                if (targetDropzoneId) {
                    const targetDropzone = dropzones.value.get(targetDropzoneId);
                    if (targetDropzone) {
                        const targetIndex = state.dragOverIndex ?? (targetDropzone.getItems()?.length ?? 0);
                        executeDrop(targetDropzoneId, targetIndex, payload);
                    }
                }
            }

            endDrag();
        };

        const handleWindowDragEnd = () => {
            if (!state.isDragging) {
                endDrag();
            }
        };

        const handleWindowDragLeave = (event) => {
            // Leaving browser window typically yields a null relatedTarget.
            if (!event.relatedTarget) {
                if (state.isDragging) {
                    hideGhostElement();
                    return;
                }

                if (!state.externalPreviewActive) return;
                hideExternalPreview();
            }
        };

        const handleWindowBlur = () => {
            if (!state.isDragging && state.externalPreviewActive) {
                hideExternalPreview();
            }
        };

        const handleStorageChange = (event) => {
            if (event.key === getCrossWindowDragStorageKey()) {
                if (!event.newValue) {
                    state.externalDragPayload = null;

                    if (!state.isDragging && state.externalPreviewActive) {
                        endDrag();
                    }
                    return;
                }

                try {
                    const parsed = JSON.parse(event.newValue);
                    state.externalDragPayload = parsed?.payload ?? null;
                } catch (e) {
                    state.externalDragPayload = null;
                }

                return;
            }

            if (event.key !== getCrossWindowMoveStorageKey() || !event.newValue) return;

            try {
                const parsed = JSON.parse(event.newValue);
                const payload = parsed?.payload;

                if (payload?.targetDropzoneId != null) {
                    executeDrop(payload.targetDropzoneId, payload.targetIndex ?? 0, payload, false);
                }
            } catch (e) {
                // Ignore malformed optimistic updates
            }
        };

        window.addEventListener('dragenter', handleWindowDragEnter);
        window.addEventListener('dragover', handleWindowDragOver);
        window.addEventListener('dragleave', handleWindowDragLeave);
        window.addEventListener('drop', handleWindowDrop);
        window.addEventListener('dragend', handleWindowDragEnd);
        window.addEventListener('blur', handleWindowBlur);
        window.addEventListener('storage', handleStorageChange);

        windowDragListenersBound = true;
    };

    const createGhostElement = (dragElement, event, explicitOffsetX = null, explicitOffsetY = null, explicitWidth = null) => {
        if (!dragElement) return;

        if (state.ghostElement) {
            state.ghostElement.remove();
            state.ghostElement = null;
        }

        const rect = dragElement.getBoundingClientRect();
        state.ghostOffsetX = explicitOffsetX ?? (event.clientX - rect.left);
        state.ghostOffsetY = explicitOffsetY ?? (event.clientY - rect.top);
        state.lastValidX = event.clientX;
        state.lastValidY = event.clientY;

        const ghost = dragElement.cloneNode(true);
        ghost.classList.add('drag-ghost-element');
        ghost.style.position = 'fixed';
        ghost.style.pointerEvents = 'none';
        ghost.style.zIndex = '9999';
        ghost.style.opacity = '0.9';
        ghost.style.transform = 'rotate(1deg) scale(1.02)';
        ghost.style.transition = 'none';
        ghost.style.willChange = 'left, top';
        document.body.appendChild(ghost);
        const baseWidth = explicitWidth ?? rect.width ?? ghost.offsetWidth ?? dragElement.offsetWidth;
        ghost.style.width = `${Math.round(baseWidth)}px`;
        state.ghostElement = ghost;

        updateGhostPosition(event);
    };

    const hideGhostElement = () => {
        if (state.ghostElement) {
            state.ghostElement.remove();
            state.ghostElement = null;
        }
    };

    const hideExternalPreview = () => {
        hideGhostElement();

        state.externalPreviewActive = false;
        state.draggedItem = null;
        state.draggedIndex = null;
        state.sourceDropzoneId = null;
        state.targetDropzoneId = null;
        state.dragOverIndex = null;
        state.ghostOffsetX = 0;
        state.ghostOffsetY = 0;
        state.lastValidX = 0;
        state.lastValidY = 0;
    };

    const startDrag = (item, index, dropzoneId, event) => {
        state.isDragging = true;
        state.draggedItem = item;
        state.draggedIndex = index;
        state.sourceDropzoneId = dropzoneId;

        // Use currentTarget to get the element the listener is attached to (not a child)
        const dragElement = event.currentTarget || event.target;

        const dragRect = dragElement?.getBoundingClientRect?.();
        const payload = {
            itemId: item?.id,
            index,
            dropzoneId,
            dragOffsetX: dragRect ? event.clientX - dragRect.left : null,
            dragOffsetY: dragRect ? event.clientY - dragRect.top : null,
            dragSnapshotWidth: dragRect?.width ?? dragElement?.offsetWidth ?? null,
            dragSnapshotHtml: dragElement?.outerHTML ?? null,
        };
        setStoredExternalDragPayload(payload);

        createGhostElement(dragElement, event, null, null, payload.dragSnapshotWidth);
    };

    const startExternalDrag = (payload, event) => {
        if (!payload?.itemId) return false;

        state.externalDragPayload = payload;
        state.externalPreviewActive = true;
        state.draggedItem = { id: payload.itemId };
        state.draggedIndex = payload.index ?? null;
        state.sourceDropzoneId = payload.dropzoneId ?? null;

        const dragElement = document.querySelector(`[data-movement-id="${payload.itemId}"]`);

        if (dragElement) {
            const fallbackOffsetX = Math.round(dragElement.offsetWidth / 2);
            const fallbackOffsetY = Math.round(dragElement.offsetHeight / 2);
            createGhostElement(
                dragElement,
                event,
                payload.dragOffsetX ?? fallbackOffsetX,
                payload.dragOffsetY ?? fallbackOffsetY,
                payload.dragSnapshotWidth,
            );
        } else if (payload.dragSnapshotHtml) {
            const snapshotWrapper = document.createElement('div');
            snapshotWrapper.innerHTML = payload.dragSnapshotHtml;
            const snapshotElement = snapshotWrapper.firstElementChild;

            if (snapshotElement) {
                createGhostElement(
                    snapshotElement,
                    event,
                    payload.dragOffsetX ?? 20,
                    payload.dragOffsetY ?? 20,
                    payload.dragSnapshotWidth,
                );
            }
        } else {
            const ghost = document.createElement('div');
            ghost.classList.add('drag-ghost-element');
            ghost.style.position = 'fixed';
            ghost.style.pointerEvents = 'none';
            ghost.style.zIndex = '9999';
            ghost.style.opacity = '0.9';
            ghost.style.transform = 'rotate(1deg) scale(1.02)';
            ghost.style.padding = '12px 18px';
            ghost.style.borderRadius = '999px';
            ghost.style.background = '#111';
            ghost.style.color = '#fff';
            ghost.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)';
            ghost.textContent = `Dragging movement ${payload.itemId}`;
            document.body.appendChild(ghost);
            state.ghostElement = ghost;
            state.ghostOffsetX = payload.dragOffsetX ?? 20;
            state.ghostOffsetY = payload.dragOffsetY ?? 20;
            updateGhostPosition(event);
        }

        return true;
    };

    const updateGhostPosition = (event) => {
        if (!state.ghostElement) return;

        const hasPointerCoords = event.clientX !== 0 || event.clientY !== 0;

        if (hasPointerCoords) {
            // Store last valid position from live pointer coordinates
            state.lastValidX = event.clientX;
            state.lastValidY = event.clientY;
        }

        const cursorX = hasPointerCoords
            ? event.clientX
            : (state.lastValidX || Math.floor(window.innerWidth / 2));
        const cursorY = hasPointerCoords
            ? event.clientY
            : (state.lastValidY || Math.floor(window.innerHeight / 2));

        // Position ghost relative to where user grabbed it
        const x = cursorX - state.ghostOffsetX;
        const y = cursorY - state.ghostOffsetY;

        state.ghostElement.style.left = `${x}px`;
        state.ghostElement.style.top = `${y}px`;
    };

    const endDrag = () => {
        // Remove ghost element
        if (state.ghostElement) {
            state.ghostElement.remove();
            state.ghostElement = null;
        }

        state.isDragging = false;
        state.draggedItem = null;
        state.draggedIndex = null;
        state.sourceDropzoneId = null;
        state.targetDropzoneId = null;
        state.hoveredDropzoneId = null;
        state.dragOverIndex = null;
        state.ghostOffsetX = 0;
        state.ghostOffsetY = 0;
        state.lastValidX = 0;
        state.lastValidY = 0;
        state.externalDragPayload = null;
        state.externalPreviewActive = false;
        setStoredExternalDragPayload(null);
    };

    const setDragOver = (dropzoneId, index) => {
        state.targetDropzoneId = dropzoneId;
        state.dragOverIndex = index;
    };

    const setHoveredDropzone = (dropzoneId) => {
        state.hoveredDropzoneId = dropzoneId;
    };

    const clearHoveredDropzone = () => {
        state.hoveredDropzoneId = null;
    };

    const executeDrop = (targetDropzoneId, targetIndex, dragPayload = null, persist = true) => {
        const payload = dragPayload || {
            itemId: state.draggedItem?.id,
            index: state.draggedIndex,
            dropzoneId: state.sourceDropzoneId,
        };

        if (!payload || !payload.dropzoneId) return false;

        const sourceDropzone = dropzones.value.get(payload.dropzoneId);
        const targetDropzone = dropzones.value.get(targetDropzoneId);

        if (!sourceDropzone || !targetDropzone) return false;

        const sourceItems = [...sourceDropzone.getItems()];
        const targetItems = payload.dropzoneId === targetDropzoneId
            ? sourceItems
            : [...targetDropzone.getItems()];

        const resolvedIndex = payload.itemId !== undefined && payload.itemId !== null
            ? sourceItems.findIndex((item) => String(item?.id) === String(payload.itemId))
            : -1;
        const draggedIndex = resolvedIndex !== -1 ? resolvedIndex : payload.index;

        if (draggedIndex === undefined || draggedIndex === null || draggedIndex < 0 || draggedIndex >= sourceItems.length) {
            return false;
        }

        // Remove from source
        const [movedItem] = sourceItems.splice(draggedIndex, 1);

        if (payload.dropzoneId === targetDropzoneId) {
            // Same list reorder
            const adjustedIndex = targetIndex > draggedIndex ? targetIndex - 1 : targetIndex;
            sourceItems.splice(adjustedIndex, 0, movedItem);
            sourceDropzone.setItems(sourceItems);

            // Trigger reorder callback
            if (sourceDropzone.onReorder) {
                sourceDropzone.onReorder(sourceItems);
            }
        } else {
            // Cross-list move
            targetItems.splice(targetIndex, 0, movedItem);
            sourceDropzone.setItems(sourceItems);
            targetDropzone.setItems(targetItems);

            // Trigger reorder callbacks for both
            if (sourceDropzone.onReorder) {
                sourceDropzone.onReorder(sourceItems);
            }
            if (targetDropzone.onReorder) {
                targetDropzone.onReorder(targetItems);
            }
        }

        if (persist) {
            setStoredCrossWindowMovePayload({
                ...payload,
                targetDropzoneId,
                targetIndex,
            });
        }

        return true;
    };

    const context = {
        state,
        dropzones,
        registerDropzone,
        unregisterDropzone,
        startDrag,
        updateGhostPosition,
        endDrag,
        setDragOver,
        setHoveredDropzone,
        clearHoveredDropzone,
        executeDrop,
        startExternalDrag,
    };

    provide(DRAG_DROP_KEY, context);

    bindWindowDragListeners();

    return context;
}

/**
 * Use the drag and drop context in child components
 */
export function useDragDropContext() {
    const context = inject(DRAG_DROP_KEY);
    if (!context) {
        throw new Error('useDragDropContext must be used within a component that has called createDragDropContext');
    }
    return context;
}

/**
 * Optional accessor for environments where drag/drop is not provided.
 */
export function useOptionalDragDropContext() {
    return inject(DRAG_DROP_KEY, null);
}

/**
 * Composable for making an element draggable
 */
export function useDraggable(item, index, dropzoneId) {
    const context = useDragDropContext();

    const onDragStart = (event) => {
        const dragRect = event.currentTarget?.getBoundingClientRect?.();
        const payload = JSON.stringify({
            itemId: item.value?.id,
            index: index.value,
            dropzoneId: dropzoneId.value,
            dragOffsetX: dragRect ? event.clientX - dragRect.left : null,
            dragOffsetY: dragRect ? event.clientY - dragRect.top : null,
            dragSnapshotWidth: dragRect?.width ?? event.currentTarget?.offsetWidth ?? null,
            dragSnapshotHtml: event.currentTarget?.outerHTML ?? null,
        });

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', payload);
        event.dataTransfer.setData('text', payload);

        // Hide default drag image
        const emptyImg = document.createElement('img');
        emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        event.dataTransfer.setDragImage(emptyImg, 0, 0);

        context.startDrag(item.value, index.value, dropzoneId.value, event);
    };

    const onDrag = (event) => {
        context.updateGhostPosition(event);
    };

    const onDragEnd = () => {
        context.endDrag();
    };

    return {
        draggableProps: {
            draggable: true,
            onDragstart: onDragStart,
            onDrag: onDrag,
            onDragend: onDragEnd,
        },
        isDragging: () => context.state.isDragging && context.state.draggedItem?.id === item.value?.id,
    };
}

/**
 * Composable for creating a dropzone
 */
export function useDropzone(dropzoneId, items, setItems, onReorder, options = {}) {
    const context = useDragDropContext();
    const isDragOver = ref(false);
    const dragOverIndex = ref(null);
    const allowSameListReorder = options.allowSameListReorder ?? true;

    // Get the actual ID value (handles both refs and computed refs)
    const getId = () => typeof dropzoneId === 'object' && 'value' in dropzoneId ? dropzoneId.value : dropzoneId;

    // Register this dropzone
    context.registerDropzone(getId(), {
        getItems: () => items.value,
        setItems: (newItems) => setItems(newItems),
        onReorder,
    });

    const onDragEnter = (event) => {
        event.preventDefault();
        isDragOver.value = true;

        if (!context.state.isDragging && !context.state.externalPreviewActive) {
            const payload = parseDragPayload(event) || context.state.externalDragPayload;
            if (payload) {
                context.startExternalDrag(payload, event);
            }
        }
    };

    const onDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        isDragOver.value = true;

        if (!context.state.isDragging && !context.state.externalPreviewActive) {
            const payload = parseDragPayload(event) || context.state.externalDragPayload;
            if (payload) {
                context.startExternalDrag(payload, event);
            }
        }

        context.updateGhostPosition(event);
    };

    const onDragLeave = (event) => {
        // Only set false if we're leaving the dropzone entirely
        if (!event.currentTarget.contains(event.relatedTarget)) {
            isDragOver.value = false;
            dragOverIndex.value = null;
        }
    };

    const onDrop = (event, targetIndex = null) => {
        event.preventDefault();
        isDragOver.value = false;

        const payload = parseDragPayload(event) || context.state.externalDragPayload;

        if (payload?.dropzoneId === getId() && !allowSameListReorder) {
            context.endDrag();
            dragOverIndex.value = null;
            return;
        }

        const index = targetIndex !== null ? targetIndex : items.value.length;
        context.executeDrop(getId(), index, payload);
        context.endDrag();
        dragOverIndex.value = null;
    };

    const setDragOverIndex = (index) => {
        dragOverIndex.value = index;
        context.setDragOver(getId(), index);
    };

    const cleanup = () => {
        context.unregisterDropzone(getId());
    };

    return {
        dropzoneProps: {
            onDragenter: onDragEnter,
            onDragover: onDragOver,
            onDragleave: onDragLeave,
            onDrop: (e) => onDrop(e),
        },
        isDragOver,
        dragOverIndex,
        setDragOverIndex,
        onDrop,
        cleanup,
        state: context.state,
    };
}
